import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { SupabaseProvider } from './context/SupabaseContext';
import { I18nProvider } from './components/common/I18nProvider';
import { WorkingHoursProvider } from './context/WorkingHoursContext';
import { LicensingProvider } from './components/licensing/LicensingProvider'; // Add this import
import { EntitlementGate } from './components/licensing/EntitlementGate';
import { BillingRecoveryRoute } from './components/licensing/BillingRecoveryRoute';
import { ProtectedRoute, AdminProtectedRoute, AdminLikeProtectedRoute } from './components/auth/ProtectedRoute';
import { SuperAdminProtectedRoute } from './components/auth/SuperAdminProtectedRoute';
import { Toaster } from 'react-hot-toast';
import { LoadingState } from './components/common/LoadingState';
import { GrowthReadOnlyRoute } from './components/growth/GrowthReadOnlyRoute';
import { GrowthCompatibilityRoute } from './components/growth/GrowthCompatibilityRoute';

const DashboardLayout = lazy(() =>
  import('./layouts/DashboardLayout').then((module) => ({ default: module.DashboardLayout }))
);
const SuperAdminLayout = lazy(() =>
  import('./layouts/SuperAdminLayout').then((module) => ({ default: module.SuperAdminLayout }))
);
const AuthLayout = lazy(() =>
  import('./layouts/AuthLayout').then((module) => ({ default: module.AuthLayout }))
);
const BookingPage = lazy(() =>
  import('./pages/booking/BookingPage').then((module) => ({ default: module.BookingPage }))
);
const BookingConfirmationPage = lazy(() =>
  import('./pages/booking/BookingConfirmationPage').then((module) => ({ default: module.BookingConfirmationPage }))
);
const LoginPage = lazy(() =>
  import('./pages/auth/LoginPage').then((module) => ({ default: module.LoginPage }))
);
const RegisterPage = lazy(() =>
  import('./pages/auth/RegisterPage').then((module) => ({ default: module.RegisterPage }))
);
const ForgotPasswordPage = lazy(() =>
  import('./pages/auth/ForgotPasswordPage').then((module) => ({ default: module.ForgotPasswordPage }))
);
const PasswordSetupPage = lazy(() =>
  import('./pages/auth/PasswordSetupPage').then((module) => ({ default: module.PasswordSetupPage }))
);
const ManageBookingPage = lazy(() =>
  import('./pages/public/ManageBookingPage').then((module) => ({ default: module.ManageBookingPage }))
);
const AuthCallbackPage = lazy(() =>
  import('./pages/public/UtilityPages').then((module) => ({ default: module.AuthCallbackPage }))
);
const LegalDocumentPage = lazy(() =>
  import('./pages/public/UtilityPages').then((module) => ({ default: module.LegalDocumentPage }))
);
const NotFoundPage = lazy(() =>
  import('./pages/public/UtilityPages').then((module) => ({ default: module.NotFoundPage }))
);
const VerifyEmailPage = lazy(() =>
  import('./pages/public/UtilityPages').then((module) => ({ default: module.VerifyEmailPage }))
);
const MarketingUnsubscribePage = lazy(() =>
  import('./pages/public/UtilityPages').then((module) => ({ default: module.MarketingUnsubscribePage }))
);
const MarketingConsentRequestPage = lazy(() =>
  import('./pages/public/UtilityPages').then((module) => ({ default: module.MarketingConsentRequestPage }))
);

const DashboardHome = lazy(() =>
  import('./pages/dashboard/DashboardHome').then((module) => ({ default: module.DashboardHome }))
);
const AppointmentDetailsPage = lazy(() =>
  import('./pages/dashboard/appointments/AppointmentDetailsPage').then((module) => ({ default: module.AppointmentDetailsPage }))
);
const CalendarPage = lazy(() =>
  import('./pages/dashboard/appointments/CalendarPage').then((module) => ({ default: module.CalendarPage }))
);
const ClientsPage = lazy(() =>
  import('./pages/dashboard/clients/ClientsPage').then((module) => ({ default: module.ClientsPage }))
);
const ClientDetailsPage = lazy(() =>
  import('./pages/dashboard/clients/ClientDetailsPage').then((module) => ({ default: module.ClientDetailsPage }))
);
const ServicesPage = lazy(() =>
  import('./pages/dashboard/salon/ServicesPage').then((module) => ({ default: module.ServicesPage }))
);
const StaffPage = lazy(() =>
  import('./pages/dashboard/staff/StaffPage').then((module) => ({ default: module.StaffPage }))
);
const AnalyticsPage = lazy(() =>
  import('./pages/dashboard/analytics/AnalyticsPage').then((module) => ({ default: module.AnalyticsPage }))
);
const InventoryPage = lazy(() =>
  import('./pages/dashboard/inventory/InventoryPage').then((module) => ({ default: module.InventoryPage }))
);
const ProductDetailsPage = lazy(() =>
  import('./pages/dashboard/inventory/ProductDetailsPage').then((module) => ({ default: module.ProductDetailsPage }))
);
const SettingsPage = lazy(() =>
  import('./pages/dashboard/settings/SettingsPage').then((module) => ({ default: module.SettingsPage }))
);
const BookingSettingsPage = lazy(() =>
  import('./pages/dashboard/settings/BookingSettingsPage').then((module) => ({ default: module.BookingSettingsPage }))
);
const EmailSettingsPage = lazy(() =>
  import('./pages/dashboard/settings/EmailSettingsPage').then((module) => ({ default: module.EmailSettingsPage }))
);
const ProfilePage = lazy(() =>
  import('./pages/dashboard/profile/ProfilePage').then((module) => ({ default: module.ProfilePage }))
);
const WorkflowEditorPage = lazy(() => import('./pages/workflows/WorkflowEditorPage'));
const WorkflowCreationPage = lazy(() => import('./pages/workflows/WorkflowCreationPage'));
const InboxPage = lazy(() =>
  import('./pages/dashboard/inbox/InboxPage').then((module) => ({ default: module.InboxPage }))
);
const BillingPage = lazy(() =>
  import('./pages/billing/BillingPage').then((module) => ({ default: module.BillingPage }))
);
const SuccessPage = lazy(() =>
  import('./pages/billing/SuccessPage').then((module) => ({ default: module.SuccessPage }))
);
const CancelPage = lazy(() =>
  import('./pages/billing/CancelPage').then((module) => ({ default: module.CancelPage }))
);
const NotificationSettingsPage = lazy(() =>
  import('./pages/dashboard/settings/notifications/NotificationSettingsPage').then((module) => ({ default: module.NotificationSettingsPage }))
);
const VouchersPage = lazy(() =>
  import('./pages/dashboard/vouchers/VouchersPage').then((module) => ({ default: module.VouchersPage }))
);
const WaitlistPage = lazy(() =>
  import('./pages/dashboard/waitlist/WaitlistPage').then((module) => ({ default: module.WaitlistPage }))
);
const ReviewsPage = lazy(() =>
  import('./pages/dashboard/reviews/ReviewsPage').then((module) => ({ default: module.ReviewsPage }))
);
const GrowthDashboardPage = lazy(() =>
  import('./pages/dashboard/growth/GrowthDashboardPage').then((module) => ({ default: module.GrowthDashboardPage }))
);
const GrowthOpportunitiesPage = lazy(() =>
  import('./pages/dashboard/growth/GrowthOpportunitiesPage').then((module) => ({ default: module.GrowthOpportunitiesPage }))
);
const GrowthOpportunityDetailPage = lazy(() =>
  import('./pages/dashboard/growth/GrowthOpportunityDetailPage').then((module) => ({ default: module.GrowthOpportunityDetailPage }))
);
const GrowthCampaignReviewPage = lazy(() =>
  import('./pages/dashboard/growth/GrowthCampaignReviewPage').then((module) => ({ default: module.GrowthCampaignReviewPage }))
);
const GrowthCampaignsPage = lazy(() =>
  import('./pages/dashboard/growth/GrowthCampaignsPage').then((module) => ({ default: module.GrowthCampaignsPage }))
);
const GrowthOwnerCampaignPage = lazy(() =>
  import('./pages/dashboard/growth/GrowthOwnerCampaignPage').then((module) => ({ default: module.GrowthOwnerCampaignPage }))
);
const GrowthAutomationsPage = lazy(() =>
  import('./pages/dashboard/growth/GrowthAutomationsPage').then((module) => ({ default: module.GrowthAutomationsPage }))
);
const GrowthAutomationDetailPage = lazy(() =>
  import('./pages/dashboard/growth/GrowthAutomationDetailPage').then((module) => ({ default: module.GrowthAutomationDetailPage }))
);
const GrowthHistoryPage = lazy(() =>
  import('./pages/dashboard/growth/GrowthHistoryPage').then((module) => ({ default: module.GrowthHistoryPage }))
);
const GrowthOutcomeDetailPage = lazy(() =>
  import('./pages/dashboard/growth/GrowthOutcomeDetailPage').then((module) => ({ default: module.GrowthOutcomeDetailPage }))
);
const GrowthActivityHistoryPage = lazy(() =>
  import('./pages/dashboard/growth/GrowthActivityHistoryPage').then((module) => ({ default: module.GrowthActivityHistoryPage }))
);
const MarketingPage = lazy(() =>
  import('./pages/dashboard/marketing/MarketingPage').then((module) => ({ default: module.MarketingPage }))
);
const MarketingCampaignsPage = lazy(() =>
  import('./pages/dashboard/marketing/campaigns/MarketingCampaignsPage').then((module) => ({ default: module.MarketingCampaignsPage }))
);
const MarketingCampaignDetailsPage = lazy(() =>
  import('./pages/dashboard/marketing/campaigns/MarketingCampaignDetailsPage').then((module) => ({ default: module.MarketingCampaignDetailsPage }))
);
const MarketingCampaignBuilderPage = lazy(() =>
  import('./pages/dashboard/marketing/campaigns/MarketingCampaignBuilderPage').then((module) => ({ default: module.MarketingCampaignBuilderPage }))
);
const MarketingAudiencesPage = lazy(() =>
  import('./pages/dashboard/marketing/audiences/MarketingAudiencesPage').then((module) => ({ default: module.MarketingAudiencesPage }))
);
const MarketingAudienceEditorPage = lazy(() =>
  import('./pages/dashboard/marketing/audiences/MarketingAudienceEditorPage').then((module) => ({ default: module.MarketingAudienceEditorPage }))
);
const MarketingAutomationsPage = lazy(() =>
  import('./pages/dashboard/marketing/automations/MarketingAutomationsPage').then((module) => ({ default: module.MarketingAutomationsPage }))
);
const MarketingAutomationEditorPage = lazy(() =>
  import('./pages/dashboard/marketing/automations/MarketingAutomationEditorPage').then((module) => ({ default: module.MarketingAutomationEditorPage }))
);
const MarketingRecipeAutomationPage = lazy(() =>
  import('./pages/dashboard/marketing/automations/MarketingRecipeAutomationPage').then((module) => ({ default: module.MarketingRecipeAutomationPage }))
);
const MarketingRecipeCampaignPage = lazy(() =>
  import('./pages/dashboard/marketing/campaigns/MarketingRecipeCampaignPage').then((module) => ({ default: module.MarketingRecipeCampaignPage }))
);
const MarketingTemplatesPage = lazy(() =>
  import('./pages/dashboard/marketing/templates/MarketingTemplatesPage').then((module) => ({ default: module.MarketingTemplatesPage }))
);
const MarketingPromotionsPage = lazy(() =>
  import('./pages/dashboard/marketing/promotions/MarketingPromotionsPage').then((module) => ({ default: module.MarketingPromotionsPage }))
);
const MarketingTemplateEditorPage = lazy(() =>
  import('./pages/dashboard/marketing/templates/MarketingTemplateEditorPage').then((module) => ({ default: module.MarketingTemplateEditorPage }))
);
const SuperAdminDashboardPage = lazy(() =>
  import('./pages/admin/SuperAdminDashboardPage').then((module) => ({ default: module.SuperAdminDashboardPage }))
);


function AppRoutes() {
  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          className:
            'rounded-2xl bg-honey-white/75 text-honey-ink ring-1 ring-honey-border shadow-soft backdrop-blur-md',
          success: {
            iconTheme: {
              primary: 'rgb(var(--honey-primary))',
              secondary: 'rgb(var(--honey-white))',
            },
            className:
              'rounded-2xl bg-honey-white/75 text-honey-ink ring-1 ring-honey-border shadow-soft backdrop-blur-md',
          },
          loading: {
            className:
              'rounded-2xl bg-honey-white/75 text-honey-ink ring-1 ring-honey-border shadow-soft backdrop-blur-md',
          },
          error: {
            className:
              'rounded-2xl bg-honey-white/75 text-honey-ink ring-1 ring-honey-border shadow-soft backdrop-blur-md',
          },
        }}
      />
      <Suspense fallback={<LoadingState className="min-h-screen" />}>
      <Routes>
        {/* Auth Routes - NO licensing enforcement */}
        <Route path="/" element={<AuthLayout />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="password-setup" element={<PasswordSetupPage />} />
          <Route path="password-reset" element={<PasswordSetupPage />} />
        </Route>

        {/* Public Booking Routes - NO licensing enforcement */}
        <Route path="/booking" element={<BookingPage />} />
        <Route path="/manage-booking" element={<ManageBookingPage />} />
        <Route path="/booking/confirmation" element={<BookingConfirmationPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/unsubscribe/:token" element={<MarketingUnsubscribePage />} />
        <Route path="/marketing-consent/:token" element={<MarketingConsentRequestPage />} />
        <Route path="/legal/privacy" element={<LegalDocumentPage kind="privacy" />} />
        <Route path="/legal/terms" element={<LegalDocumentPage kind="terms" />} />

        <Route
          path="/admin"
          element={
            <SuperAdminProtectedRoute redirectTo="/">
              <SuperAdminLayout />
            </SuperAdminProtectedRoute>
          }
        >
          <Route index element={<SuperAdminDashboardPage />} />
        </Route>
        
        {/* Protected Dashboard Routes - WITH licensing enforcement */}
        <Route path="/" element={
          <ProtectedRoute>
            <LicensingProvider>
              <Outlet />
            </LicensingProvider>
          </ProtectedRoute>
        }>
          <Route element={
            <EntitlementGate>
              <DashboardLayout />
            </EntitlementGate>
          }>
            <Route index element={<DashboardHome />} />
          <Route path="/inbox" element={<InboxPage />} />
          <Route path="appointments">
            <Route index element={<Navigate to="/calendar" replace />} />
            <Route path=":id" element={<AppointmentDetailsPage />} />
          </Route>
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="waitlist" element={<WaitlistPage />} />
          <Route path="reviews" element={<ReviewsPage />} />
          <Route path="clients">
            <Route index element={<ClientsPage />} />
            <Route path="new" element={<ClientsPage isNew={true} />} />
            <Route path=":id" element={<ClientDetailsPage />} />
          </Route>
          <Route path="staff">
            <Route index element={<StaffPage />} />
          </Route>
          <Route path="services" element={<ServicesPage />} />
          <Route path="inventory">
            <Route index element={<InventoryPage />} />
            <Route path=":id" element={<ProductDetailsPage />} />
          </Route>
          <Route path="vouchers" element={<VouchersPage />} />
          <Route path="/workflows/new" element={<WorkflowCreationPage />} />
          <Route path="/workflows" element={<WorkflowEditorPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="growth" element={
            <AdminLikeProtectedRoute redirectTo="/">
              <GrowthReadOnlyRoute>
                <Outlet />
              </GrowthReadOnlyRoute>
            </AdminLikeProtectedRoute>
          }>
            <Route index element={<GrowthDashboardPage />} />
            <Route path="opportunities" element={<GrowthOpportunitiesPage />} />
            <Route path="opportunities/:id" element={<GrowthOpportunityDetailPage />} />
            <Route path="campaigns" element={<GrowthCampaignsPage />} />
            <Route path="campaigns/new" element={<GrowthOwnerCampaignPage />} />
            <Route path="campaigns/:id" element={<GrowthCampaignReviewPage />} />
            <Route path="automations" element={<GrowthAutomationsPage />} />
            <Route path="automations/:id" element={<GrowthAutomationDetailPage />} />
            <Route path="history" element={<GrowthHistoryPage />} />
            <Route path="history/activity" element={<GrowthActivityHistoryPage />} />
            <Route path="history/:id" element={<GrowthOutcomeDetailPage />} />
          </Route>
          <Route path="marketing" element={
            <AdminLikeProtectedRoute redirectTo="/">
              <Outlet />
            </AdminLikeProtectedRoute>
          }>
            <Route index element={
              <GrowthCompatibilityRoute growthPath="/growth" preserveWhenSearch>
                <MarketingPage />
              </GrowthCompatibilityRoute>
            } />
            <Route path="campaigns" element={
              <GrowthCompatibilityRoute growthPath="/growth/campaigns" preserveWhenSearch>
                <MarketingCampaignsPage />
              </GrowthCompatibilityRoute>
            } />
            <Route path="campaigns/new" element={
              <GrowthCompatibilityRoute growthPath="/growth/campaigns/new" preserveWhenSearch>
                <MarketingRecipeCampaignPage />
              </GrowthCompatibilityRoute>
            } />
            <Route path="campaigns/new/advanced" element={<MarketingCampaignBuilderPage />} />
            <Route path="campaigns/:id" element={<MarketingCampaignDetailsPage />} />
            <Route path="automations" element={
              <GrowthCompatibilityRoute growthPath="/growth/automations" preserveWhenSearch>
                <MarketingAutomationsPage />
              </GrowthCompatibilityRoute>
            } />
            <Route path="automations/new" element={
              <GrowthCompatibilityRoute growthPath="/growth/automations" preserveWhenSearch>
                <MarketingRecipeAutomationPage />
              </GrowthCompatibilityRoute>
            } />
            <Route path="automations/new/advanced" element={<MarketingAutomationEditorPage />} />
            <Route path="automations/:id" element={<MarketingAutomationEditorPage />} />
            <Route path="audiences" element={<MarketingAudiencesPage />} />
            <Route path="audiences/new" element={<MarketingAudienceEditorPage />} />
            <Route path="audiences/:id" element={<MarketingAudienceEditorPage />} />
            <Route path="templates" element={<MarketingTemplatesPage />} />
            <Route path="templates/new" element={<MarketingTemplateEditorPage />} />
            <Route path="templates/:id" element={<MarketingTemplateEditorPage />} />
            <Route path="promotions" element={<MarketingPromotionsPage />} />
          </Route>
          <Route path="profile" element={<ProfilePage />} />
          <Route path="/profile/:id" element={<ProfilePage />} />
          <Route path="settings" element={
            <AdminProtectedRoute redirectTo="/">
              <Outlet />
            </AdminProtectedRoute>
          }>
            <Route index element={<SettingsPage />} />
            <Route path="booking" element={<BookingSettingsPage />} />
            <Route path="email" element={<EmailSettingsPage />} />
            <Route path="business" element={<Navigate to="/settings/booking" replace />} />
            <Route
              path="notifications"
              element={<NotificationSettingsPage />}
            />
          </Route>

          </Route>

          {/* Billing Routes - NO licensing enforcement (accessible even when blocked) */}
          <Route element={
            <BillingRecoveryRoute>
              <DashboardLayout />
            </BillingRecoveryRoute>
          }>
            <Route path="/billing" element={<BillingPage />} />
            <Route path="/billing/success" element={<SuccessPage />} />
            <Route path="/billing/cancel" element={<CancelPage />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <SupabaseProvider>
          <WorkingHoursProvider>
            <AppRoutes />
          </WorkingHoursProvider>
        </SupabaseProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

export default App;